export type RequestStatus = 'recue' | 'en-traitement' | 'resolue'

export interface TimelineStep {
  date: string
  label: string
}

export interface RequestAttachment {
  name: string
  /** Drives the icon shown next to the file. */
  type: 'image' | 'pdf' | 'form'
  size: string
}

export type MessageAuthor = 'citoyen' | 'ville'

export interface RequestMessage {
  id: string
  author: MessageAuthor
  authorName: string
  sentAt: string
  body: string
  attachment?: RequestAttachment
  /** Set when the Ville expects something back from the citizen. */
  awaiting?: 'document' | 'info'
}

export interface CitizenRequest {
  id: string
  reference: string
  title: string
  /** Service municipal responsable (ex. Service de la voirie). */
  service: string
  /** Sous-catégorie de la requête (ex. Collectes). */
  category: string
  address: string
  fullAddress: string
  submittedOn: string
  createdOn: string
  resolvedOn: string | null
  createdBy: string
  status: RequestStatus
  description: string
  attachments: RequestAttachment[]
  timeline: TimelineStep[]
  messages: RequestMessage[]
  response?: string
}

export const statusLabels: Record<RequestStatus, string> = {
  recue: 'Reçue',
  'en-traitement': 'En traitement',
  resolue: 'Résolue',
}

export const statusOrder: RequestStatus[] = ['recue', 'en-traitement', 'resolue']

export const quickReports = ['Voirie / nid-de-poule', 'Éclairage', 'Aqueduc', 'Déneigement', 'Collectes', 'Autre']

export const profileAddress = '112, route 138'

/** Very small keyword classifier used to simulate ATNA's understanding. */
export const intents = [
  {
    keywords: ['nid-de-poule', 'nid de poule', 'trou', 'asphalte', 'route', 'chaussée'],
    label: 'Nid-de-poule',
    service: 'Service de la voirie',
    question: 'Depuis quand avez-vous remarqué le problème ?',
    answers: ['Aujourd’hui', 'Cette semaine', 'Depuis plus d’un mois'],
    photoHint: true,
  },
  {
    keywords: ['lampadaire', 'lumière', 'éclairage', 'ampoule'],
    label: 'Lampadaire défectueux',
    service: 'Éclairage public',
    question: 'Le lampadaire est-il complètement éteint ou clignote-t-il ?',
    answers: ['Complètement éteint', 'Il clignote', 'Il reste allumé le jour'],
    photoHint: false,
  },
  {
    keywords: ['neige', 'déneigement', 'gratte', 'banc de neige', 'glace'],
    label: 'Déneigement',
    service: 'Service de la voirie',
    question: 'Où se situe le problème ?',
    answers: ['Devant mon entrée', 'Sur le chemin', 'Autour de mon bac'],
    photoHint: true,
  },
  {
    keywords: ['eau', 'fuite', 'coule', 'aqueduc', 'pression'],
    label: 'Problème d’aqueduc',
    service: 'L’aqueduc',
    question: 'L’écoulement est-il continu ?',
    answers: ['Oui, en continu', 'Par intermittence', 'Je ne sais pas'],
    photoHint: true,
  },
  {
    keywords: ['bac', 'collecte', 'ordures', 'recyclage', 'ramassé', 'centre de tri'],
    label: 'Bac non ramassé',
    service: 'Matières résiduelles',
    question: 'Quel bac n’a pas été ramassé ?',
    answers: ['Ordures', 'Recyclage', 'Les deux'],
    photoHint: false,
  },
]

export const emergencyKeywords = [
  'fil électrique',
  'fil electrique',
  'électrocut',
  'feu',
  'incendie',
  'gaz',
  'blessé',
  'accident',
  'fumée',
]

export const requests: CitizenRequest[] = [
  {
    id: 'req-2903',
    reference: '#REQ-2903',
    title: 'Chat errant signalé',
    service: 'Municipalité',
    category: 'Animal errant ou trouvé',
    address: 'Route 138, près du bureau municipal',
    fullAddress: '28, route 138, Baie-Trinité, Québec, G0H 1A0',
    submittedOn: 'Envoyée aujourd’hui',
    createdOn: '18/09/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'recue',
    description:
      'Chat errant aperçu près du bureau municipal depuis quelques jours. L’animal est craintif mais ne semble pas blessé.',
    attachments: [{ name: 'chat-errant-138.jpg', type: 'image', size: '2,1 Mo' }],
    timeline: [{ date: 'Aujourd’hui', label: 'Signalement reçu' }],
    messages: [
      {
        id: 'msg-2903-1',
        author: 'ville',
        authorName: 'Municipalité',
        sentAt: 'Aujourd’hui, 9 h 42',
        body: 'Merci pour votre signalement. Des journées de stérilisation de chats errants sont prévues les 25 et 26 septembre : nous ajoutons ce signalement à la liste.',
      },
    ],
  },
  {
    id: 'req-2841',
    reference: '#REQ-2841',
    title: 'Nid-de-poule',
    service: 'Service de la voirie',
    category: 'Voirie et chaussée',
    address: '112, route 138',
    fullAddress: '112, route 138, Baie-Trinité, Québec, G0H 1A0',
    submittedOn: 'Envoyée le 26 août',
    createdOn: '26/08/26',
    resolvedOn: null,
    createdBy: 'ATNA',
    status: 'en-traitement',
    description:
      'Important nid-de-poule devant la propriété, en bordure de la route 138. Le trou s’est élargi depuis les dernières pluies et les véhicules l’évitent en empiétant sur la voie opposée.',
    attachments: [
      { name: 'nid-de-poule-route-138.jpg', type: 'image', size: '1,8 Mo' },
      { name: 'vue-de-la-route.jpg', type: 'image', size: '2,4 Mo' },
    ],
    timeline: [
      { date: '26 août', label: 'Demande reçue' },
      { date: '27 août', label: 'Transmise au service de la voirie' },
      { date: '28 août', label: 'En traitement' },
      { date: '31 août', label: 'Inspection planifiée' },
    ],
    messages: [
      {
        id: 'msg-2841-1',
        author: 'ville',
        authorName: 'Voirie · Julie Béland',
        sentAt: '27 août, 10 h 15',
        body: 'Bonjour, votre signalement a été transmis à l’équipe de voirie. Une inspection est prévue cette semaine.',
      },
      {
        id: 'msg-2841-2',
        author: 'citoyen',
        authorName: 'Vous',
        sentAt: '28 août, 8 h 03',
        body: 'Merci. Le trou s’est encore agrandi depuis la pluie de dimanche, il fait maintenant environ 60 cm de large.',
      },
      {
        id: 'msg-2841-3',
        author: 'ville',
        authorName: 'Voirie · Julie Béland',
        sentAt: '29 août, 14 h 30',
        body: 'Merci pour la précision. Pouvez-vous confirmer si le nid-de-poule se trouve devant votre entrée ou plus à l’ouest ? Une photo prise de loin nous aiderait à le localiser.',
        awaiting: 'info',
      },
      {
        id: 'msg-2841-4',
        author: 'ville',
        authorName: 'Voirie · Julie Béland',
        sentAt: '31 août, 9 h 05',
        body: 'Une réclamation pour dommage à un véhicule peut être ouverte si applicable. Veuillez remplir et nous retourner le formulaire ci-joint dans les 15 jours.',
        attachment: { name: 'formulaire-reclamation-dommages.pdf', type: 'form', size: '184 Ko' },
        awaiting: 'document',
      },
    ],
  },
  {
    id: 'req-2790',
    reference: '#REQ-2790',
    title: 'Lampadaire défectueux',
    service: 'Service de la voirie',
    category: 'Éclairage public',
    address: '128, route 138',
    fullAddress: '128, route 138, Baie-Trinité, Québec, G0H 1A0',
    submittedOn: 'Envoyée le 12 août',
    createdOn: '12/08/26',
    resolvedOn: '18/08/26',
    createdBy: 'ATNA',
    status: 'resolue',
    description:
      'Le lampadaire devant le 128 restait éteint la nuit, laissant ce tronçon de la route 138 dans le noir.',
    attachments: [],
    timeline: [
      { date: '12 août', label: 'Demande reçue' },
      { date: '13 août', label: 'Transmise au service de l’éclairage' },
      { date: '18 août', label: 'Résolue' },
    ],
    messages: [
      {
        id: 'msg-2790-1',
        author: 'ville',
        authorName: 'Éclairage public',
        sentAt: '18 août, 16 h 20',
        body: 'L’ampoule a été remplacée aujourd’hui par l’équipe d’entretien. La requête est fermée, n’hésitez pas à nous écrire si le problème revient.',
      },
    ],
    response: 'L’ampoule a été remplacée le 18 août par l’équipe d’entretien.',
  },
]

export type PublicReportCategory = 'danger' | 'voirie' | 'eclairage' | 'arbre'

export type PublicReportStatus = 'nouveau' | 'en-traitement' | 'resolue'

export interface PublicReport {
  id: string
  category: PublicReportCategory
  title: string
  address: string
  distance: string
  reportedAgo: string
  status: PublicReportStatus
  confirmations: number
  lat: number
  lng: number
}

export const reportCategories: Record<PublicReportCategory, { label: string; color: string }> = {
  danger: { label: 'Danger', color: '#c8322b' },
  voirie: { label: 'Voirie', color: '#b45309' },
  eclairage: { label: 'Éclairage', color: '#8a6212' },
  arbre: { label: 'Arbres', color: '#276b43' },
}

export const publicReportStatusLabels: Record<PublicReportStatus, string> = {
  nouveau: 'Nouveau',
  'en-traitement': 'Équipe en route',
  resolue: 'Réglé',
}

/** Position approximative du domicile au profil (route 138, Baie-Trinité). */
export const profileCoordinates: [number, number] = [49.4186, -67.2986]

/**
 * Cinq signalements seulement : à l'échelle du village, une liste plus longue
 * ne serait pas crédible.
 */
export const publicReports: PublicReport[] = [
  {
    id: 'pub-1',
    category: 'voirie',
    title: 'Nid-de-poule profond',
    address: 'Route 138, près du bureau municipal',
    distance: '300 m',
    reportedAgo: 'il y a 25 min',
    status: 'nouveau',
    confirmations: 4,
    lat: 49.4208,
    lng: -67.295,
  },
  {
    id: 'pub-2',
    category: 'eclairage',
    title: 'Lampadaire éteint',
    address: '128, route 138',
    distance: '450 m',
    reportedAgo: 'il y a 2 h',
    status: 'en-traitement',
    confirmations: 6,
    lat: 49.4162,
    lng: -67.3027,
  },
  {
    id: 'pub-3',
    category: 'arbre',
    title: 'Branche tombée en bordure du chemin',
    address: 'Chemin du Vieux-Phare',
    distance: '650 m',
    reportedAgo: 'il y a 40 min',
    status: 'nouveau',
    confirmations: 3,
    lat: 49.423,
    lng: -67.3036,
  },
  {
    id: 'pub-4',
    category: 'voirie',
    title: 'Affaissement de chaussée',
    address: 'Route 138, entrée est du village',
    distance: '1,1 km',
    reportedAgo: 'il y a 5 h',
    status: 'en-traitement',
    confirmations: 9,
    lat: 49.4121,
    lng: -67.2906,
  },
  {
    id: 'pub-5',
    category: 'danger',
    title: 'Accotement affaissé près du centre de tri',
    address: 'Route 138, secteur du centre de tri',
    distance: '1,4 km',
    reportedAgo: 'il y a 15 min',
    status: 'nouveau',
    confirmations: 11,
    lat: 49.4252,
    lng: -67.2871,
  },
]

export const detailQuestions = [
  'Où en est ma demande ?',
  'Quand sera-t-elle traitée ?',
  'Est-ce que je dois faire autre chose ?',
]
